import React from 'react';
import { Link } from 'react-router-dom';
import { useCMS } from '../context/CMSContext';
import Meta from '../components/Meta';

export default function Forside() {
  const { getPage, testimonials, loading } = useCMS();
  const page = getPage('index');

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-brand-red border-t-transparent" />
      </div>
    );
  }

  // Fallback content if CMS is not seeded or fails
  const title = page?.title || "Chr. Nielsen & Søn V/Leif Nielsen tilbyder køletransport i Jylland samt resten af Danmark | Chr. Nielsen & Søn V/Leif Nielsen";
  const metaDescription = page?.meta_description || "Chr. Nielsen & Søn V/Leif Nielsen tilbyder køletransport i Jylland samt resten af Danmark. Kontakt Chr. Nielsen & Søn V/Leif Nielsen i dag!";
  const headline = page?.headline || "Chr. Nielsen & Søn V/Leif Nielsen tilbyder køletransport i Jylland samt resten af Danmark";
  const contentHtml = page?.content || `
    <p>Med mange års erfaring kombineret med det nyeste transportmateriel kan vi tilbyde vores kunder en optimal transportmæssig løsning i forbindelse med køle og godstransport.</p>
    <p>Chr. Nielsen & Søn er en mindre transportvirksomhed. Vi har ingen stor administration. Dette kommer vores kunder tilgode gennem fornuftige priser, høj kvalitet og stor fleksibilitet.</p>
  `;

  // Features / Advantages
  const features = [
    {
      icon: 'fa-solid fa-temperature-arrow-down',
      title: 'Præcis Temperaturstyring',
      desc: 'Vores moderne kølevogne sikrer en ubrudt kølekæde under hele transporten, uanset om det gælder køle- eller frysevarer.',
      color: 'text-brand-green',
      borderColor: 'border-brand-green/20'
    },
    {
      icon: 'fa-solid fa-truck-fast',
      title: 'Høj Fleksibilitet',
      desc: 'Vi kører både ad hoc opgaver og faste kontraktkørsler. Vi tilpasser os dine logistiske behov og tidsplaner.',
      color: 'text-brand-red',
      borderColor: 'border-brand-red/20'
    },
    {
      icon: 'fa-solid fa-shield-check',
      title: 'Driftssikkerhed & Kvalitet',
      desc: 'Med 10 moderne vogne og erfarne chauffører garanterer vi, at dine varer ankommer sikkert og til tiden.',
      color: 'text-brand-green',
      borderColor: 'border-brand-green/20'
    },
    {
      icon: 'fa-solid fa-handshake',
      title: 'Fornuftige Priser',
      desc: 'Vi har ingen stor og tung administration. Det betyder direkte kontakt, hurtig beslutningstagen og yderst konkurrencedygtige priser.',
      color: 'text-brand-red',
      borderColor: 'border-brand-red/20'
    }
  ];

  // Selected gallery images
  const galleryImages = [
    'https://assets.auxodev.dk/assets/c3c69980-e6b4-4d46-bdae-26d6f6795887/1.jpg',
    'https://assets.auxodev.dk/assets/c3c69980-e6b4-4d46-bdae-26d6f6795887/2.jpg',
    'https://assets.auxodev.dk/assets/c3c69980-e6b4-4d46-bdae-26d6f6795887/3.jpg'
  ];

  return (
    <>
      <Meta title={title} description={metaDescription} isHome={true} />

      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden py-20">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=80,format=auto/assets/c3c69980-e6b4-4d46-bdae-26d6f6795887/hero-koelebil.jpg" 
            alt="Køletransport Danmark" 
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-dark via-transparent to-transparent" />
        </div>

        <div className="relative z-10 mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            {/* Left Content */}
            <div className="lg:col-span-7 space-y-8 text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-brand-red/10 border border-brand-red/30 rounded-full text-brand-red text-xs font-bold uppercase tracking-wider">
                <span className="h-2 w-2 rounded-full bg-brand-red animate-pulse" />
                Professionel Køletransport
              </div>
              
              <h1 className="text-2xl sm:text-4xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.1] uppercase">
                {headline}
              </h1>

              <div 
                className="text-lg sm:text-xl text-gray-300 font-normal leading-relaxed max-w-2xl space-y-4"
                dangerouslySetInnerHTML={{ __html: contentHtml }}
              />

              <div className="flex flex-wrap gap-4 pt-4">
                <Link 
                  to="/kontakt" 
                  className="px-8 py-4 bg-brand-red hover:bg-brand-red-hover text-white rounded-md font-bold text-base tracking-wide transition-all duration-200 shadow-lg shadow-brand-red/20 hover:scale-[1.02] active:scale-[0.98]"
                >
                  Kontakt os
                </Link>
                <a 
                  href="tel:40535984" 
                  className="px-8 py-4 bg-transparent hover:bg-white/5 border-2 border-white/20 hover:border-white text-white rounded-md font-bold text-base tracking-wide transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] flex items-center gap-2"
                >
                  <i className="fa-solid fa-phone" />
                  Ring nu
                </a>
              </div>
            </div>

            {/* Right Contact Box */}
            <div className="lg:col-span-5">
              <div className="bg-brand-dark-card/95 border-2 border-brand-red/30 rounded-lg p-8 shadow-2xl backdrop-blur-sm relative overflow-hidden group">
                {/* Decorative red corner line */}
                <div className="absolute top-0 right-0 w-16 h-16 border-t-4 border-r-4 border-brand-red" />
                <div className="absolute bottom-0 left-0 w-16 h-16 border-b-4 border-l-4 border-brand-green" />

                <h3 className="text-2xl font-bold text-white uppercase tracking-wide mb-6">
                  Direkte Kontakt
                </h3>
                <p className="text-sm text-gray-400 mb-8 leading-relaxed">
                  Har du brug for et uforpligtende tilbud eller akut køletransport? Vi sidder klar til at hjælpe dig.
                </p>

                <div className="space-y-6">
                  {/* Telefonboks (Primær) */}
                  <a 
                    href="tel:40535984" 
                    className="block p-5 bg-brand-red hover:bg-brand-red-hover text-white rounded-md transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-brand-red/10"
                  >
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 rounded-full bg-white/10 flex items-center justify-center text-xl">
                        <i className="fa-solid fa-phone animate-bounce" />
                      </div>
                      <div className="text-left">
                        <div className="text-xs uppercase tracking-widest text-red-100 font-bold leading-none mb-1">Telefon (Primær)</div>
                        <div className="text-lg sm:text-2xl font-extrabold tracking-wide whitespace-nowrap">40 53 59 84</div>
                      </div>
                    </div>
                  </a>

                  {/* Mailboks */}
                  <a 
                    href="mailto:info@chrnielsen.dk" 
                    className="block p-5 bg-brand-dark border border-brand-green/30 hover:border-brand-green text-white rounded-md transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                  >
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 rounded-full bg-brand-green/10 flex items-center justify-center text-xl text-brand-green">
                        <i className="fa-solid fa-envelope" />
                      </div>
                      <div className="text-left">
                        <div className="text-xs uppercase tracking-widest text-gray-400 font-bold leading-none mb-1">E-mail</div>
                        <div className="text-base sm:text-xl font-extrabold text-brand-green tracking-wide whitespace-nowrap">info@chrnielsen.dk</div>
                      </div>
                    </div>
                  </a>
                </div>

                <div className="mt-8 pt-6 border-t border-brand-dark-border flex items-center justify-between text-xs text-gray-500">
                  <span><i className="fa-solid fa-clock text-brand-green mr-1" /> Døgnvagt v/ akutte behov</span>
                  <span>CVR: 19047938</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Presentation & Advantages Section */}
      <section className="py-24 bg-brand-dark border-t border-brand-dark-border relative">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
          
          {/* Section Header */}
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <h2 className="text-xs font-bold uppercase tracking-[0.25em] text-brand-red">
              Hvorfor vælge os
            </h2>
            <p className="text-xl sm:text-3xl md:text-4xl font-extrabold text-white uppercase tracking-tight">
              Sikker kølekæde og høj leveringssikkerhed
            </p>
            <div className="h-1 w-20 bg-brand-red mx-auto mt-4" />
          </div>

          {/* Advantages Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feat, idx) => (
              <div 
                key={idx} 
                className={`bg-brand-dark-card border ${feat.borderColor} rounded-lg p-8 transition-all duration-300 hover:border-white/10`}
              >
                <div className={`text-3xl ${feat.color} mb-6`}>
                  <i className={feat.icon} />
                </div>
                <h3 className="text-lg font-bold text-white uppercase tracking-wide mb-3">
                  {feat.title}
                </h3>
                <p className="text-sm text-gray-400 leading-relaxed">
                  {feat.desc}
                </p>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Call To Action Section */}
      <section className="relative py-20 overflow-hidden bg-brand-dark-card border-t border-b border-brand-dark-border">
        {/* Diagonal red/green accent lines */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-brand-red via-brand-green to-brand-red" />
        
        <div className="relative z-10 mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="text-left max-w-3xl space-y-4">
              <h2 className="text-xl sm:text-3xl font-extrabold text-white uppercase tracking-tight">
                Har du brug for en pålidelig transportpartner?
              </h2>
              <p className="text-gray-400 text-base leading-relaxed">
                Uanset om du har brug for en enkeltstående køletransport eller en fast aftale om kontraktkørsel, står vi klar med vores vognpark af topmoderne kølevogne.
              </p>
            </div>
            <div className="flex flex-wrap gap-4 flex-shrink-0">
              <Link 
                to="/kontakt" 
                className="px-8 py-4 bg-brand-red hover:bg-brand-red-hover text-white rounded-md font-bold text-base tracking-wide transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
              >
                Få et uforpligtende tilbud
              </Link>
              <a 
                href="tel:40535984" 
                className="px-8 py-4 bg-brand-dark border border-brand-green/30 hover:border-brand-green text-brand-green rounded-md font-bold text-base tracking-wide transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] flex items-center gap-2"
              >
                <i className="fa-solid fa-phone" />
                Ring til os nu
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Selected Images Grid */}
      <section className="py-24 bg-brand-dark">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
          
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <h2 className="text-xs font-bold uppercase tracking-[0.25em] text-brand-green">
              Vores Vognpark & Logistik
            </h2>
            <p className="text-xl sm:text-4xl font-extrabold text-white uppercase tracking-tight">
              Moderne materiel i aktion
            </p>
            <div className="h-1 w-20 bg-brand-green mx-auto mt-4" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {galleryImages.map((img, idx) => (
              <div 
                key={idx} 
                className="relative aspect-[4/3] rounded-lg overflow-hidden border border-brand-dark-border group"
              >
                <img 
                  src={`https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=90,format=auto/${img.replace('https://assets.auxodev.dk/', '')}`} 
                  alt="Køletransport Chr. Nielsen & Søn" 
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                  <div className="text-left">
                    <p className="text-xs font-bold text-brand-green uppercase tracking-widest mb-1">Chr. Nielsen & Søn</p>
                    <p className="text-sm font-bold text-white uppercase">Pålidelig køletransport</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Testimonials Infinite Slider */}
      <section className="py-24 bg-brand-dark-card border-t border-b border-brand-dark-border overflow-hidden">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8 mb-12">
          <div className="text-center max-w-3xl mx-auto space-y-4">
            <h2 className="text-xs font-bold uppercase tracking-[0.25em] text-brand-red">
              Kundeanmeldelser
            </h2>
            <p className="text-xl sm:text-4xl font-extrabold text-white uppercase tracking-tight">
              Hvad vores kunder siger
            </p>
            <div className="h-1 w-20 bg-brand-red mx-auto mt-4" />
          </div>
        </div>

        {/* Infinite Slider Container */}
        <div className="relative w-full overflow-hidden py-4">
          {/* Fade overlays for smooth edges */}
          <div className="absolute left-0 top-0 bottom-0 w-20 sm:w-40 bg-gradient-to-r from-brand-dark-card to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-20 sm:w-40 bg-gradient-to-l from-brand-dark-card to-transparent z-10 pointer-events-none" />

          <div className="animate-infinite-scroll gap-6">
            {/* Double the items to create seamless loop */}
            {[...testimonials, ...testimonials].map((test, idx) => (
              <div 
                key={idx} 
                className="w-[300px] sm:w-[400px] bg-brand-dark border border-brand-dark-border rounded-lg p-8 flex-shrink-0 flex flex-col justify-between"
              >
                <div className="space-y-4">
                  {/* Stars */}
                  <div className="flex gap-1 text-amber-500">
                    {[...Array(test.rating)].map((_, i) => (
                      <i key={i} className="fa-solid fa-star text-sm" />
                    ))}
                  </div>
                  <p className="text-sm text-gray-300 italic leading-relaxed">
                    "{test.text}"
                  </p>
                </div>
                <div className="mt-6 pt-4 border-t border-brand-dark-border text-left">
                  <h4 className="text-sm font-bold text-white uppercase tracking-wide">
                    {test.name}
                  </h4>
                  <p className="text-xs text-brand-green font-medium">
                    {test.company}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
